import { CommandGroup, CommandItem } from '@/components/ui/command';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { useDealContext } from '@/contexts/deal-provider';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import useLenders from '@/hooks/use-lenders';
import { HOTKEYS } from '@/lib/hotkeys';
import { Plus } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ComboboxPopover } from '../../ui/combobox/combobox-popover';
import DealLenderBadge from './deal-lender-bagde';
import LenderDialog from './lender-dialog';
import useLenderMutations from './use-lender-mutations';
import useLenderOptions from './use-lender-options';

const DealLenderSelector = ({
  withHotkeyListener,
  className,
}: {
  withHotkeyListener?: boolean;
  className?: string;
}) => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const dealId = deal?.id;
  const lender = deal?.lender;

  const [comboBoxPopoverIsOpen, setComboBoxPopoverIsOpen] = useState(false);

  const { lenders } = useLenders();

  const { lenderOptions, getLenderOptionIDByValue } = useLenderOptions();

  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const { createLenderMutation, updateLenderMutation, deleteLenderMutation } =
    useLenderMutations({
      dealId,
      dealShortId: deal?.short_id,
      setDialogIsOpen,
    });

  const handleLenderChange = (value: string) => {
    if (value === 'delete' && lender) {
      deleteLenderMutation.mutate({
        lender,
      });
      return;
    }

    const innerLender = lenders.find(
      (lender) => lender.id === getLenderOptionIDByValue(value),
    );

    if (!innerLender) {
      return;
    } else {
      updateLenderMutation.mutate({
        lender: innerLender,
      });
    }
  };

  const buttonRef = useRef<HTMLDivElement>(null);

  const currentOption = useMemo(() => {
    return lenderOptions.find(
      (option) => option.id === (lender?.id ?? 'delete'),
    );
  }, [lenderOptions, lender]);

  return (
    <div>
      <ComboboxPopover
        open={comboBoxPopoverIsOpen}
        onOpenChange={setComboBoxPopoverIsOpen}
        endAdornment={<KeyboardKeyIcon>{HOTKEYS.DEAL_LENDER}</KeyboardKeyIcon>}
        placeholder="Set lender..."
        onChange={handleLenderChange}
        emptyMessage="No lender found"
        currentOption={currentOption}
        options={lenderOptions}
        footerOptions={
          <CommandGroup>
            <p className="p-2 rounded text-xs  text-gray-600">New lender</p>
            <CommandItem
              onSelect={() => {
                setComboBoxPopoverIsOpen(false);
                setDialogIsOpen(true);
              }}
            >
              <Plus className="h-3.5 w-3.5" />
              <p className="rounded text-xs  text-gray-600">New lender</p>
            </CommandItem>
          </CommandGroup>
        }
        side="right"
        align="start"
      >
        <div ref={buttonRef} className={className}>
          <DealLenderBadge lender={lender} />
          {withHotkeyListener && (
            <HotKeyListener
              hotkeys={HOTKEYS.DEAL_LENDER}
              onHotkeyPressed={() => buttonRef.current?.click()}
            />
          )}
        </div>
      </ComboboxPopover>
      <LenderDialog
        initialName={''}
        initialEmail={''}
        initialPhone={''}
        initialLenderType={'bank'}
        onClickCta={({
          nameValue,
          emailValue,
          phoneValue,
          lenderTypeValue,
        }) => {
          createLenderMutation.mutate({
            lender: {
              id: uuidv4(),
              name: nameValue,
              email: emailValue,
              phone: phoneValue,
              lender_type: lenderTypeValue,
            },
          });
        }}
        ctaText={'Create Lender'}
        onOpen={() => setDialogIsOpen(true)}
        onClose={() => setDialogIsOpen(false)}
        dialogIsOpen={dialogIsOpen}
      ></LenderDialog>
    </div>
  );
};

export default DealLenderSelector;
